import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { bePayInvoiceWrite } from 'actions/payment/bePayInvoice.action';
import ICO_INVALID from 'assets/images/icons/invalid-ico.png';
import IMG_CLIP from 'assets/images/icons/paper-clip.svg';
import SalonModal from 'components/dialog/SalonModal.component';
import SM from 'components/dialog/SalonModal.module.scss';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Foldout from 'components/generic/Foldout.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import Tooltip from 'components/generic/Tooltip.component';
import CurrencySign from 'components/price/CurrencySign.component';
import INVOICE_STATUS from 'constants/invoiceStatus.const';
import { usePrepareReadWrite } from 'hooks/usePrepare.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectBillingInvoices } from 'selectors/settings.selectors';
import { locationReload } from 'utils/dom/location.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

import { GetCustomerInvoices } from 'settings/billing/actions';

import CN from './InvoiceTable.module.scss';

const L = logger('InvoiceTable');

const TooltipContent = ({ t }) => (
  <div data-bem="TooltipContent--InvoiceTable" className={CN.tooltipText}>
    {t('staffAndBusiness.subAndBilling.invoices.tooltipContent')}
  </div>
);

export const InvoiceTable = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { formatDate, isSalonInGB } = useGilt();
  const salonId = useCurrentSalonId();

  const dispatch = useDispatch();
  const invoices = useSelector(selectBillingInvoices);

  const { up: payInvoice } = usePrepareReadWrite({ writer: bePayInvoiceWrite });

  const { val: isModalRendered, yay: showModal, nay: hideModal } = useTip(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    success: true,
  });

  const overdueInvoices = isFullArray(invoices?.results) ? invoices.results.filter((inv) => inv?.should_pay).length : 0;

  const handlePayInvoice = async (invoiceId) => {
    try {
      const response = await payInvoice({ data: { invoiceId } });
      L.debug('handlePayInvoice()', response);
      const errorMsg = response.originalJson?.errors?.error;

      if (errorMsg) {
        setModalContent({
          title: t('staffAndBusiness.subAndBilling.invoices.modalTitle1'),
          message: t('staffAndBusiness.subAndBilling.invoices.modalDescription1', { errorMsg }),
          success: false,
        });
        showModal();
        return;
      }

      const isPending = response?.has_pending_invoices;

      if (isPending) {
        setModalContent({
          title: t('staffAndBusiness.subAndBilling.invoices.modalTitle2'),
          message: t('staffAndBusiness.subAndBilling.invoices.modalDescription2'),
          success: true,
        });
        showModal();
      } else {
        locationReload({ tag: 'InvoiceTable' });
        // fetchInvoices(salonId, {});
      }
    } catch (error) {
      setModalContent({
        title: t('staffAndBusiness.subAndBilling.invoices.modalTitle1'),
        message: t('staffAndBusiness.subAndBilling.invoices.modalDescription3', { error }),
        success: false,
      });
      showModal();
    }
  };

  const hasMoreInvoices = invoices.next;
  const hasPreviousInvoice = invoices.previous;

  const AmountExcVAT = ({ amount }) => <CurrencySign value={amount / 1.2 / 100} />;
  const Amount = ({ amount }) => <CurrencySign value={amount / 100} />;

  const loadMoreInvoices = useCallback(() => {
    if (hasMoreInvoices) {
      const startingAfter = invoices.next;
      dispatch(GetCustomerInvoices(salonId, { starting_after: startingAfter }));
    }
  }, [hasMoreInvoices, invoices.next, dispatch, salonId]);

  const loadPreviousInvoices = useCallback(() => {
    if (invoices.previous) {
      const startingBefore = invoices.previous;
      dispatch(GetCustomerInvoices(salonId, { starting_before: startingBefore }));
    }
  }, [invoices.previous, dispatch, salonId]);

  return (
    isFullArray(invoices?.results) && (
      <div
        data-bem="InvoiceTable"
        className={cn({
          [CN.component]: true,
          [CN.darkMode]: isDarkMode,
        })}
      >
        <Foldout
          isOpen={false}
          title={
            <div className={CN.foldOutTitle}>
              <span>{t('staffAndBusiness.subAndBilling.invoices.foldoutTitle')}</span>
              {overdueInvoices >= 1 && (
                <Badge tag="InvoiceTable" variant="restricted" height="sm">
                  <span className={CN.badgeFont}>
                    {overdueInvoices} {t('composite.badge.overdue')}
                  </span>
                </Badge>
              )}
            </div>
          }
          src={IMG_CLIP}
        >
          <div className={CN.note}>{t('staffAndBusiness.subAndBilling.invoices.description')}</div>
          {overdueInvoices >= 1 && (
            <div className={CN.alertContainer}>
              <AlertPanel tag="InvoiceTable__alert--error" variant="error">
                <div className={CN.alert}>
                  <img src={ICO_INVALID} className={CN.alertIco} alt="Pay the overdue invoice" />
                  {overdueInvoices > 1 ? t('staffAndBusiness.subAndBilling.invoices.overdueAlertPlural') : t('staffAndBusiness.subAndBilling.invoices.overdueAlert')}
                </div>
              </AlertPanel>
            </div>
          )}

          <div className={CN.outer} data-bem="InvoiceTable__table">
            <div className={CN.tableContainer}>
              <div className={CN.tableHeader} data-bem="InvoiceTable__table--header">
                <div className={CN.cellHeader}>
                  <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.invoiceDate')}</div>
                </div>
                <div className={CN.cellHeader}>
                  <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.periodCovered')}</div>
                </div>
                <div className={CN.cellHeader}>
                  <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.status')}</div>
                </div>
                <div className={CN.cellHeaderBig}>
                  <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.type')}</div>
                </div>
                <div className={CN.cellHeaderSmall}>
                  <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.amountExcTax')}</div>
                </div>
                {isSalonInGB && (
                  <div className={CN.cellHeaderSmall}>
                    <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.amountIncTax')}</div>
                  </div>
                )}
                <div className={CN.cellHeaderSmall}>
                  <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.amountRefunded')}</div>
                </div>
                <div className={CN.cellHeader}>
                  <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.datePaid')}</div>
                </div>
                <div className={CN.cellHeader}>
                  <div>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.dateRefunded')}</div>
                </div>
                <div className={CN.headerFixedCell}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.downloadDetails')}</div>
              </div>

              <div className={CN.scrollBody} data-bem="body">
                {invoices.results.map((invoice) => {
                  const displayBgnEndDate = invoice?.period_start ? (
                    <div>
                      {formatDate(invoice.period_start, { hideTime: true, hideWeekday: true })}
                      {' - '}
                      {invoice?.period_end && <div className={CN.newLine}>{formatDate(invoice.period_end, { hideTime: true, hideWeekday: true })}</div>}
                    </div>
                  ) : (
                    '— —'
                  );
                  const displayPaidDate = formatDate(invoice?.paid_date, { hideTime: true, hideWeekday: true });
                  const displayRefundDate = formatDate(invoice?.refund_date, { hideTime: true, hideWeekday: true });

                  return (
                    <div className={CN.tableRow} data-bem="InvoiceTable__table--row" key={invoice.id}>
                      <div className={CN.cell}>
                        <span className={CN.mobileLabelHeader}>{formatDate(invoice.created, { hideTime: true, hideWeekday: true })}</span>
                      </div>
                      <div className={CN.cell}>
                        <span className={CN.mobileLabel}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.periodCovered')}:</span>
                        {displayBgnEndDate}
                      </div>
                      <div
                        className={cn({
                          [CN.cell]: true,
                          [CN.column]: true,
                        })}
                      >
                        <span className={CN.mobileLabel}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.status')}:</span>
                        {invoice.status === INVOICE_STATUS.partiallyRefunded && (
                          <Tooltip variant="leftToBottom" width="min" content={<TooltipContent t={t} />}>
                            <div className={CN.iconContainer}>
                              <Icon variant="info" width="sm" /> <Badge variant="sky" name={t('composite.badge.partRefunded')} />
                            </div>
                          </Tooltip>
                        )}
                        {invoice.status === INVOICE_STATUS.void && <Badge variant="alert" name={t('composite.badge.void')} height="sm" />}
                        {invoice.status === INVOICE_STATUS.paid && <Badge variant="success" name={t('composite.badge.paid')} height="sm" />}
                        {invoice.status === INVOICE_STATUS.overdue && <Badge variant="restricted" name={t('composite.badge.overdue')} height="sm" />}
                        {invoice.status === INVOICE_STATUS.refunded && <Badge variant="unlimited" name={t('composite.badge.refunded')} height="sm" />}
                        {invoice.status === INVOICE_STATUS.open && <Badge variant="restricted" name={t('composite.badge.overdue')} height="sm" />}
                        <ResponsiveBox.Desktop>
                          {invoice.should_pay && (
                            <Button variant="secondary" onClick={() => handlePayInvoice(invoice.id)} height="xs" noMargin>
                              <span className={CN.buttonPayInvoiceText}>{t('staffAndBusiness.subAndBilling.invoices.payInvoiceButton')}</span>
                            </Button>
                          )}
                        </ResponsiveBox.Desktop>
                      </div>
                      <div className={CN.cellBig}>
                        <span className={CN.mobileLabel}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.type')}:</span>
                        {invoice.invoice_type}
                      </div>
                      <div className={CN.cellSmall}>
                        <span className={CN.mobileLabel}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.amountExcTax')}:</span>
                        <AmountExcVAT amount={invoice.invoice_amount} />
                      </div>
                      {isSalonInGB && (
                        <div className={CN.cellSmall}>
                          <span className={CN.mobileLabel}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.amountIncTax')}:</span>
                          <Amount amount={invoice.invoice_amount} />
                        </div>
                      )}
                      <div className={CN.cellSmall}>
                        <span className={CN.mobileLabel}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.amountRefunded')}:</span>
                        <Amount amount={invoice.refunded_amount} />
                      </div>
                      <div className={CN.cell}>
                        <span className={CN.mobileLabel}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.datePaid')}:</span>
                        {displayPaidDate}
                      </div>
                      <div className={CN.cell}>
                        <span className={CN.mobileLabel}>{t('staffAndBusiness.subAndBilling.invoices.columnHeader.dateRefunded')}:</span>
                        {displayRefundDate}
                      </div>
                      <div className={CN.fixedColumnCell} role="button" tabIndex={0}>
                        <Button variant="bin" onClick={alwaysNoop}>
                          <ExternalLink link={invoice.invoice_pdf} download>
                            <div className={CN.pdfLinkContainer}>
                              <Icon variant="download" width="lg" />
                              <div className={CN.pdfLink}>{t('staffAndBusiness.subAndBilling.invoices.downloadButton')}</div>
                            </div>
                          </ExternalLink>
                        </Button>
                        <ResponsiveBox.Mobile>
                          {invoice.should_pay && (
                            <Button variant="primary" onClick={() => handlePayInvoice(invoice.id)} noMargin>
                              <span className={CN.buttonPayInvoiceText}>{t('staffAndBusiness.subAndBilling.invoices.payInvoiceButton')}</span>
                            </Button>
                          )}
                        </ResponsiveBox.Mobile>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={CN.tableFooter}>
                <div className={CN.fixedFooterCell}>
                  {hasPreviousInvoice && (
                    <Button variant="outline" onClick={loadPreviousInvoices}>
                      {t('staffAndBusiness.subAndBilling.invoices.previousButton')}
                    </Button>
                  )}
                  {hasMoreInvoices && (
                    <Button variant="outline" onClick={loadMoreInvoices}>
                      {hasMoreInvoices && hasPreviousInvoice ? t('common.buttons.next') : t('staffAndBusiness.subAndBilling.invoices.viewMoreButton')}
                    </Button>
                  )}
                </div>
                <div></div>
              </div>
            </div>
          </div>
        </Foldout>

        <SalonModal tag="InvoiceTable" isOpen={isModalRendered} variant="noFooter">
          <SalonModal.Header>
            <div className={SM.closeButtonContainer}>
              <Button variant="empty" onClick={hideModal}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <IconStatus width="lg" variant={modalContent.success ? 'success' : 'alert'}>
              <Icon selected width="l1" variant={modalContent.success ? 'confirm' : 'alert'} />
            </IconStatus>

            <div className={SM.modalContent}>
              <div className={SM.modalTitle}>{modalContent.title}</div>
              <div className={SM.modalText}>{modalContent.message}</div>
            </div>
          </SalonModal.Body>
        </SalonModal>
      </div>
    )
  );
};
